import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CoinListItem } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { useCoinList } from '../../hooks/useCoinList';
import { cgGet } from '../../utils/coingecko';
import { CoinSelector } from '../Transactions/CoinSelector';
import { CardHeader } from '../ui/Card';
import { InfoTooltip } from '../ui/InfoTooltip';
import { Loader2, AlertTriangle, X, Plus, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { SplitBuyChart, CHART_RANGES, type ChartRange } from './SplitBuyChart';
import { usePlannerFormat } from './plannerFormat';
import { useStrategy } from '../../context/StrategyContext';
import { useSavedBuyPlans } from '../../hooks/useSavedPlans';
import { SavedBuyPlan } from '../../types/savedPlans';
import { formatDate } from '../../utils/formatters';

const TIER_COLORS = [
  { bgClass: 'bg-blue-500',    textClass: 'text-blue-400',    borderClass: 'border-blue-500/30',    hex: '#3b82f6' },
  { bgClass: 'bg-violet-500',  textClass: 'text-violet-400',  borderClass: 'border-violet-500/30',  hex: '#8b5cf6' },
  { bgClass: 'bg-emerald-500', textClass: 'text-emerald-400', borderClass: 'border-emerald-500/30', hex: '#10b981' },
  { bgClass: 'bg-orange-500',  textClass: 'text-orange-400',  borderClass: 'border-orange-500/30',  hex: '#f97316' },
  { bgClass: 'bg-pink-500',    textClass: 'text-pink-400',    borderClass: 'border-pink-500/30',    hex: '#ec4899' },
  { bgClass: 'bg-teal-500',    textClass: 'text-teal-400',    borderClass: 'border-teal-500/30',    hex: '#14b8a6' },
  { bgClass: 'bg-yellow-500',  textClass: 'text-yellow-400',  borderClass: 'border-yellow-500/30',  hex: '#eab308' },
  { bgClass: 'bg-cyan-500',    textClass: 'text-cyan-400',    borderClass: 'border-cyan-500/30',    hex: '#06b6d4' },
  { bgClass: 'bg-rose-500',    textClass: 'text-rose-400',    borderClass: 'border-rose-500/30',    hex: '#f43f5e' },
  { bgClass: 'bg-indigo-500',  textClass: 'text-indigo-400',  borderClass: 'border-indigo-500/30',  hex: '#6366f1' },
];

function tierColor(i: number) {
  return TIER_COLORS[i % TIER_COLORS.length];
}

// Shared column template for the buy-tiers table:
// dot · target · alloc · vs-live · budget · coins · remove.
// Target/alloc inputs stay fixed + modest; the three right-aligned data columns
// (vs live, budget, coins) share the leftover width evenly, so the space between
// the inputs and their results is filled by real columns rather than a spacer.
const TIER_GRID_COLS = '14px 120px 72px minmax(84px,1fr) minmax(96px,1fr) minmax(112px,1fr) 22px';

interface Tier {
  targetPrice: string;
  alloc: string;
}

const DEFAULT_TIERS: Tier[] = [
  { targetPrice: '', alloc: '50' },
  { targetPrice: '', alloc: '30' },
  { targetPrice: '', alloc: '20' },
];

interface Props {
  embedded?: { coin: CoinListItem };
  /** Set by the Strategy tab when the user clicks a saved plan row. The nonce
   *  changes on every click so re-selecting the same plan re-triggers a load. */
  editRequest?: { plan: SavedBuyPlan; nonce: number } | null;
  /** Notifies the Strategy tab which plan id is currently loaded (or null). */
  onEditingChange?: (planId: string | null) => void;
  /** Whether this planner's sub-tab is currently visible. Leaving it clears any
   *  in-progress edit session so there's no stale state on return. */
  active?: boolean;
  /** Called with the plan id after a successful save, so the Strategy tab can
   *  scroll to and briefly highlight the saved row. Not called on the Free limit. */
  onPlanSaved?: (planId: string) => void;
}

function isPlanEmpty(budget: string, futurePrice: string, tiers: Tier[]): boolean {
  if (budget !== '' || futurePrice !== '') return false;
  return tiers.every(t => !t.targetPrice);
}

export function SplitBuyPlanner({ embedded, editRequest, onEditingChange, active, onPlanSaved }: Props = {}) {
  const { rate, currency } = useCurrency();
  const { fmtCoins, fmtPrice, fmtTotal } = usePlannerFormat();
  const { coins, loading: coinsLoading, error: coinsError } = useCoinList();
  const { buyPlan, updateBuyPlan } = useStrategy();
  const { addPlan: addSavedBuyPlan, updatePlan: updateSavedBuyPlan } = useSavedBuyPlans();

  const [planName,      setPlanName]      = useState('');
  const [savedToast,    setSavedToast]    = useState(false);
  const [savedToastMsg, setSavedToastMsg] = useState('Buy plan saved');
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [editingLabel,  setEditingLabel]  = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  // When embedded, only seed from the saved plan if it's for this coin
  const seedPlan = embedded
    ? (buyPlan?.coinId === embedded.coin.id ? buyPlan : null)
    : buyPlan;

  const [selectedCoin,     setSelectedCoin]     = useState<CoinListItem | null>(embedded ? embedded.coin : null);
  const [livePriceUsd,     setLivePriceUsd]     = useState<number | null>(null);
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [budget,           setBudget]           = useState(seedPlan?.budget      ?? '');
  const [tiers,            setTiers]            = useState<Tier[]>(seedPlan?.tiers ?? DEFAULT_TIERS);
  const [futurePrice,      setFuturePrice]      = useState(seedPlan?.futurePrice  ?? '');
  // Price-chart collapse + range are owned here so the controls can render either
  // on their own "Price chart" row (expanded) or on the Buy Tiers row (collapsed).
  const [chartExpanded,    setChartExpanded]    = useState(false);
  const [chartRange,       setChartRange]       = useState<ChartRange>('90D');

  // Fetch live price when coin changes
  useEffect(() => {
    if (!selectedCoin) { setLivePriceUsd(null); return; }
    if (selectedCoin.currentPrice > 0) { setLivePriceUsd(selectedCoin.currentPrice); return; }

    let cancelled = false;
    setLivePriceLoading(true);
    cgGet('/simple/price', { params: { ids: selectedCoin.id, vs_currencies: 'usd' }, timeout: 8_000 })
      .then(({ data }) => {
        if (cancelled) return;
        const price = (data as Record<string, { usd: number }>)[selectedCoin.id]?.usd ?? 0;
        setLivePriceUsd(price > 0 ? price : null);
      })
      .catch(() => { if (!cancelled) setLivePriceUsd(null); })
      .finally(() => { if (!cancelled) setLivePriceLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCoin]);

  // Restore selected coin from context once coins list loads (only when not embedded)
  const restoredCoinRef = useRef(false);
  useEffect(() => {
    if (embedded || restoredCoinRef.current || !coins.length || !buyPlan?.coinId) return;
    restoredCoinRef.current = true;
    const coin = coins.find(c => c.id === buyPlan.coinId) ?? null;
    if (coin) setSelectedCoin(coin);
  }, [coins]); // eslint-disable-line react-hooks/exhaustive-deps

  // When embedded route changes (different coin), re-init local state for the new coin
  useEffect(() => {
    if (!embedded) return;
    if (selectedCoin?.id === embedded.coin.id) return;
    setSelectedCoin(embedded.coin);
    if (buyPlan?.coinId === embedded.coin.id) {
      setBudget(buyPlan.budget);
      setFuturePrice(buyPlan.futurePrice);
      setTiers(buyPlan.tiers);
    } else {
      setBudget('');
      setFuturePrice('');
      setTiers(DEFAULT_TIERS);
    }
  }, [embedded?.coin.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist plan to context whenever state changes — but never overwrite a different
  // coin's plan with an empty/default one (avoids auto-trash when opening a coin page)
  useEffect(() => {
    if (!selectedCoin) return;
    if (isPlanEmpty(budget, futurePrice, tiers) && buyPlan && buyPlan.coinId !== selectedCoin.id) return;
    updateBuyPlan({
      coinId:      selectedCoin.id,
      coinName:    selectedCoin.name,
      coinSymbol:  selectedCoin.symbol,
      coinImage:   selectedCoin.image ?? '',
      budget,
      futurePrice,
      tiers,
      currencyCode: currency.code,
    });
  }, [selectedCoin?.id, budget, futurePrice, tiers, currency.code]); // eslint-disable-line react-hooks/exhaustive-deps

  // User-driven coin change exits any active edit session (programmatic loads use
  // setSelectedCoin directly, so they don't clear editing).
  const handleCoinSelect = useCallback((coin: CoinListItem) => {
    setSelectedCoin(coin);
    setEditingPlanId(null);
    setEditingLabel('');
  }, []);
  const updateTier = useCallback((index: number, field: keyof Tier, value: string) => {
    setTiers(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  }, []);
  const addTier = useCallback(() => {
    setTiers(prev => [...prev, { targetPrice: '', alloc: '' }]);
  }, []);
  const removeTier = useCallback((index: number) => {
    setTiers(prev => prev.filter((_, i) => i !== index));
  }, []);

  // ── Parsed values ──────────────────────────────────────────────────────────
  const budgetNum    = parseFloat(budget) || 0;
  const tierAllocs   = tiers.map(t => parseFloat(t.alloc) || 0);
  const tierPrices   = tiers.map(t => parseFloat(t.targetPrice) || 0);
  const allocSum     = tierAllocs.reduce((a, b) => a + b, 0);
  const allocValid   = Math.abs(allocSum - 100) < 0.01;
  const futurePriceNum = parseFloat(futurePrice) || 0;

  // ── Per-tier calcs (budget & prices are already in selected currency) ──────
  const tierResults = tiers.map((_, i) => {
    const allocated      = (tierAllocs[i] / 100) * budgetNum;
    const coinsReceived  = tierPrices[i] > 0 ? allocated / tierPrices[i] : 0;
    return { allocated, coinsReceived };
  });

  const totalCoins      = tierResults.reduce((s, t) => s + t.coinsReceived, 0);
  const avgCostBasis    = totalCoins > 0 ? budgetNum / totalCoins : 0;
  const futureValue     = totalCoins * futurePriceNum;
  const futurePnl       = futureValue - budgetNum;
  const futurePnlPct    = budgetNum > 0 ? (futurePnl / budgetNum) * 100 : 0;
  const canShowResults  = allocValid && budgetNum > 0 && !!selectedCoin && totalCoins > 0;

  // ── Chart tier overlay lines ───────────────────────────────────────────────
  const tierLines = tiers.map((_, i) => ({
    price:     tierPrices[i],
    tierIndex: i,
    color:     tierColor(i).hex,
  }));

  // Reset the planner to a fresh, blank state and exit any edit session.
  const resetPlanner = useCallback(() => {
    setEditingPlanId(null);
    setEditingLabel('');
    setPlanName('');
    setBudget('');
    setFuturePrice('');
    setTiers(DEFAULT_TIERS);
    if (!embedded) setSelectedCoin(null);
  }, [embedded]);

  // ── Save plan (frozen snapshot — never affects holdings / cost basis / P&L) ──
  const canSave = canShowResults;
  const handleSavePlan = useCallback(() => {
    if (!canSave || !selectedCoin) return;
    const snapshot = {
      coinId:     selectedCoin.id,
      coinName:   selectedCoin.name,
      coinTicker: selectedCoin.symbol.toUpperCase(),
      coinImage:  selectedCoin.image ?? '',
      totalBudget: budgetNum,
      currency:   currency.code,
      tiers: tiers.map((_, i) => ({
        price:             tierPrices[i],
        allocationPercent: tierAllocs[i],
        allocationAmount:  tierResults[i].allocated,
      })),
      tierCount:    tiers.length,
      totalCoins,
      avgCostBasis,
      planName:     planName.trim() || `Saved ${formatDate(new Date().toISOString())}`,
    };
    let savedId: string;
    if (editingPlanId) {
      updateSavedBuyPlan(editingPlanId, snapshot);
      setSavedToastMsg('Plan updated');
      savedId = editingPlanId;
    } else {
      savedId = addSavedBuyPlan(snapshot).id;
      setSavedToastMsg('Buy plan saved');
    }
    setSavedToast(true);
    resetPlanner();   // ready for a fresh entry
    onPlanSaved?.(savedId);   // scroll up to + highlight the saved row
  }, [canSave, selectedCoin, budgetNum, currency.code, tiers, tierPrices, tierAllocs, tierResults, totalCoins, avgCostBasis, planName, editingPlanId, addSavedBuyPlan, updateSavedBuyPlan, resetPlanner, onPlanSaved]);

  useEffect(() => {
    if (!savedToast) return;
    const t = setTimeout(() => setSavedToast(false), 4000);
    return () => clearTimeout(t);
  }, [savedToast]);

  // Surface the active edit target to the Strategy tab (for the row highlight).
  useEffect(() => { onEditingChange?.(editingPlanId); }, [editingPlanId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving this sub-tab while editing clears the session so nothing is stale.
  const prevActive = useRef(active);
  useEffect(() => {
    if (prevActive.current && !active && editingPlanId) resetPlanner();
    prevActive.current = active;
  }, [active, editingPlanId, resetPlanner]);

  // Load a saved plan into the planner when the Strategy tab requests an edit.
  const lastEditNonce = useRef(0);
  useEffect(() => {
    if (!editRequest || editRequest.nonce === lastEditNonce.current) return;
    lastEditNonce.current = editRequest.nonce;
    const plan = editRequest.plan;
    setSelectedCoin({
      id: plan.coinId,
      symbol: plan.coinTicker,
      name: plan.coinName,
      image: plan.coinImage ?? '',
      currentPrice: 0, marketCapRank: 0, marketCap: 0, volume24h: 0, high24h: 0, low24h: 0,
    });
    setBudget(String(plan.totalBudget));
    setFuturePrice('');
    setTiers(plan.tiers.map(t => ({ targetPrice: String(t.price), alloc: String(t.allocationPercent) })));
    setPlanName(plan.planName);
    setEditingPlanId(plan.id);
    setEditingLabel(plan.planName || plan.coinName);
    requestAnimationFrame(() => {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [editRequest]);

  const inputClass = 'w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors';
  const labelClass = 'block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider';
  // Compact input used inside the dense tiers table and the summary strip.
  const tierInputClass = 'w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-white/20 focus:border-slate-500 transition-colors';

  // Placeholder that hints the expected price magnitude for the selected asset,
  // using the live price in the display currency (mirrors the future-value input).
  const livePriceDisplay = livePriceUsd != null ? livePriceUsd * rate : null;
  const pricePlaceholder = livePriceDisplay && livePriceDisplay > 0
    ? `e.g. ${
        livePriceDisplay >= 1000 ? Math.round(livePriceDisplay).toLocaleString('en-US')
        : livePriceDisplay >= 1  ? livePriceDisplay.toLocaleString('en-US', { maximumFractionDigits: 2 })
        :                          livePriceDisplay.toLocaleString('en-US', { maximumFractionDigits: 6 })
      }`
    : '0';

  // Range buttons + collapse chevron. Rendered on the "Price chart" row when
  // expanded, or on the Buy Tiers row when collapsed (only one mounts at a time).
  const hasCoin = !!selectedCoin;
  const chartControls = (
    <div className="flex items-center gap-1">
      {hasCoin && CHART_RANGES.map(r => (
        <button
          key={r}
          type="button"
          onClick={() => setChartRange(r)}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
            chartRange === r
              ? 'bg-slate-700 text-slate-100 border border-slate-600'
              : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/60'
          }`}
        >
          {r}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setChartExpanded(v => !v)}
        className="ml-1 p-1 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/60 transition-colors"
        aria-label={chartExpanded ? 'Collapse chart' : 'Expand chart'}
        aria-expanded={chartExpanded}
      >
        {chartExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
    </div>
  );

  const inner = (
    <>
      {/* ── Header: asset + budget (budget-only when embedded) ──────────────── */}
      {embedded ? (
        <div>
          <label className={labelClass}>Total Budget ({currency.code})</label>
          <input
            type="number"
            min="0"
            value={budget}
            onChange={e => setBudget(e.target.value)}
            placeholder="e.g. 1000"
            className={inputClass}
          />
          {selectedCoin && livePriceUsd != null && (
            <p className="mt-2 text-xs text-slate-500">
              Live {selectedCoin.symbol}: <span className="text-slate-300 font-mono">{fmtPrice(livePriceUsd * rate)}</span>
            </p>
          )}
          {selectedCoin && livePriceLoading && (
            <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Fetching price…
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1.5 min-h-[16px]">
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider">Asset</label>
              {selectedCoin && (
                livePriceLoading ? (
                  <span className="flex items-center gap-1 text-[11px] text-slate-500">
                    <Loader2 className="w-3 h-3 animate-spin" /> live
                  </span>
                ) : livePriceUsd != null ? (
                  <span className="text-[11px] text-slate-500">
                    live <span className="text-slate-300 font-mono">{fmtPrice(livePriceUsd * rate)}</span>
                  </span>
                ) : null
              )}
            </div>
            <CoinSelector
              coins={coins}
              selectedId={selectedCoin?.id ?? ''}
              onSelect={handleCoinSelect}
              loading={coinsLoading}
              error={coinsError}
            />
          </div>
          <div>
            <label className={labelClass}>Total Budget ({currency.code})</label>
            <input
              type="number"
              min="0"
              value={budget}
              onChange={e => setBudget(e.target.value)}
              placeholder="e.g. 1000"
              className={inputClass}
            />
          </div>
        </div>
      )}

      {/* ── Price chart — collapsed by default. When expanded, its label + controls
          get their own row above the chart; when collapsed the controls move onto
          the Buy Tiers row below (fix 3). ── */}
      {chartExpanded && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Price chart</span>
            <div className="ml-auto">{chartControls}</div>
          </div>
          <SplitBuyChart
            coinId={selectedCoin?.id ?? ''}
            symbol={selectedCoin?.symbol ?? ''}
            tierLines={tierLines}
            range={chartRange}
          />
        </div>
      )}

      {/* ── Buy tiers table ─────────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Buy Tiers</span>
          <div className="flex items-center gap-2">
            {allocSum > 0 && (
              allocValid
                ? <span className="text-xs text-emerald-400">✓ 100% allocated</span>
                : <span className="text-xs text-amber-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{allocSum.toFixed(1)}% / 100%</span>
            )}
            {!chartExpanded && chartControls}
          </div>
        </div>

        <div className="rounded-xl border border-slate-700 overflow-hidden">
          {/* Column headers */}
          <div className="hidden sm:grid items-center gap-2 px-3 py-2 border-b border-slate-700 bg-slate-800/40" style={{ gridTemplateColumns: TIER_GRID_COLS }}>
            <span />
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Target ({currency.code})</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Alloc %</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">vs Live</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Budget</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Coins</span>
            <span />
          </div>

          {/* Tier rows — desktop grid + mobile stacked two-line variant */}
          {tiers.map((tier, i) => {
            const color  = tierColor(i);
            const result = tierResults[i];
            const targetPriceNum = tierPrices[i];
            const allocNum       = tierAllocs[i];
            const hasPrice  = targetPriceNum > 0;
            const hasBudget = budgetNum > 0;
            const hasAlloc  = allocNum > 0;
            const allocatedValue = hasBudget && hasAlloc ? result.allocated : null;
            const coinsReceived  = hasBudget && hasAlloc && hasPrice ? result.coinsReceived : null;
            const sym = selectedCoin?.symbol ?? '';

            // Display-only: how far this tier's target sits from the current live
            // price (both in the display currency). Never persisted or saved.
            const liveDisplay = livePriceUsd != null ? livePriceUsd * rate : null;
            const vsLivePct = liveDisplay != null && liveDisplay > 0 && hasPrice
              ? (targetPriceNum / liveDisplay - 1) * 100
              : null;
            const vsLiveStr = vsLivePct != null ? `${vsLivePct > 0 ? '+' : ''}${vsLivePct.toFixed(1)}%` : '—';
            // Buy side: below live (negative) is the good entry → green; above → amber.
            const vsLiveColor = vsLivePct == null ? 'text-slate-600'
              : vsLivePct < 0 ? 'text-emerald-400' : vsLivePct > 0 ? 'text-amber-400' : 'text-slate-400';

            const targetInput = (
              <input
                type="number" min="0"
                value={tier.targetPrice}
                onChange={e => updateTier(i, 'targetPrice', e.target.value)}
                placeholder={pricePlaceholder}
                aria-label={`Tier ${i + 1} target price`}
                className={tierInputClass}
              />
            );
            const allocInput = (
              <input
                type="number" min="0" max="100"
                value={tier.alloc}
                onChange={e => updateTier(i, 'alloc', e.target.value)}
                placeholder="0"
                aria-label={`Tier ${i + 1} allocation percent`}
                className={tierInputClass}
              />
            );
            const removeBtn = i > 0 ? (
              <button
                type="button"
                onClick={() => removeTier(i)}
                className="p-0.5 rounded text-slate-500 hover:text-slate-200 hover:bg-slate-700/60 transition-colors justify-self-end"
                aria-label={`Remove tier ${i + 1}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null;

            return (
              <React.Fragment key={i}>
                {/* Desktop grid row */}
                <div className="hidden sm:grid items-center gap-2 px-3 border-b border-slate-700/50" style={{ gridTemplateColumns: TIER_GRID_COLS, minHeight: 34 }}>
                  <span className={`w-2 h-2 rounded-full ${color.bgClass} flex-shrink-0`} />
                  {targetInput}
                  {allocInput}
                  <span className={`text-xs tabular-nums truncate text-right ${vsLiveColor}`}>{vsLiveStr}</span>
                  <span className="text-xs text-slate-200 tabular-nums truncate text-right">
                    {allocatedValue != null ? fmtTotal(allocatedValue) : <span className="text-slate-600">—</span>}
                  </span>
                  <span className={`text-xs tabular-nums truncate text-right ${coinsReceived != null ? color.textClass : ''}`}>
                    {coinsReceived != null
                      ? <>{fmtCoins(coinsReceived)} <span className="text-[11px] font-normal text-slate-500">{sym}</span></>
                      : <span className="text-slate-600">—</span>}
                  </span>
                  {removeBtn ?? <span />}
                </div>

                {/* Mobile stacked row — inputs on line 1, derived values on line 2 */}
                <div className="sm:hidden px-3 py-2.5 border-b border-slate-700/50">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${color.bgClass} flex-shrink-0`} />
                    <div className="flex-1">{targetInput}</div>
                    <div className="w-16 flex-shrink-0">{allocInput}</div>
                    {removeBtn}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 pl-4 text-[11px] tabular-nums text-slate-500">
                    <span>vs live <span className={vsLiveColor}>{vsLiveStr}</span></span>
                    <span>budget <span className="text-slate-300">{allocatedValue != null ? fmtTotal(allocatedValue) : '—'}</span></span>
                    <span>coins <span className="text-slate-300">{coinsReceived != null ? `${fmtCoins(coinsReceived)} ${sym}` : '—'}</span></span>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Add tier — compact last row inside the same border */}
          <button
            type="button"
            onClick={addTier}
            className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add tier
          </button>
        </div>

        {/* ── Allocation split — thin bar directly under the table ──────────── */}
        {allocSum > 0 && (
          <div className="mt-2 h-[5px] rounded-full overflow-hidden flex bg-slate-800">
            {tiers.map((tier, i) => {
              const pct        = parseFloat(tier.alloc) || 0;
              const displayPct = allocSum > 0 ? (pct / allocSum) * 100 : 0;
              if (displayPct <= 0) return null;
              return (
                <div
                  key={i}
                  className={`${tierColor(i).bgClass} transition-all duration-300`}
                  style={{ width: `${displayPct}%` }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ── Validation warning ─────────────────────────────────────────────── */}
      {!allocValid && budgetNum > 0 && allocSum > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-300">
            Results hidden until allocations sum to exactly 100% (currently {allocSum.toFixed(1)}%).
          </p>
        </div>
      )}

      {/* ── Summary + future value — one horizontal strip ───────────────────── */}
      {canShowResults && (
        <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Left — coins + combined avg/break-even (identical without fees) */}
            <div className="grid grid-cols-2 gap-4 lg:flex-none">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Total coins</p>
                <p className="text-sm font-semibold text-slate-100 tabular-nums">
                  {fmtCoins(totalCoins)} <span className="text-[11px] font-normal text-slate-500">{selectedCoin.symbol}</span>
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Avg buy price</p>
                <p className="text-sm font-semibold text-slate-100 tabular-nums">{fmtPrice(avgCostBasis)}</p>
              </div>
            </div>

            {/* Right — target price input + Value / P&L / Return (gets the room) */}
            <div className="flex flex-wrap items-end gap-4 lg:flex-1 lg:border-l lg:border-slate-700 lg:pl-4">
              <div className="flex-1 min-w-[160px]">
                <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Target price ({currency.code})
                  <InfoTooltip text="Projects what your planned coins (the total from your buy tiers above) would be worth if the price reached your target price - showing the value, profit/loss, and return." />
                </label>
                <input
                  type="number"
                  min="0"
                  value={futurePrice}
                  onChange={e => setFuturePrice(e.target.value)}
                  placeholder={livePriceUsd
                    ? `e.g. ${Math.round(livePriceUsd * rate * 2).toLocaleString()}`
                    : 'e.g. 100000'}
                  className={tierInputClass}
                />
              </div>
              {futurePriceNum > 0 && (
                <>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Value</p>
                    <p className="text-sm font-semibold text-slate-100 tabular-nums">{fmtTotal(futureValue)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">P / L</p>
                    <p className={`text-sm font-semibold tabular-nums ${futurePnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {futurePnl >= 0 ? '+' : ''}{fmtTotal(futurePnl)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Return</p>
                    <p className={`text-sm font-semibold tabular-nums ${futurePnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {futurePnlPct >= 0 ? '+' : ''}{futurePnlPct.toFixed(1)}%
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Save plan — name + button on one row ────────────────────────────── */}
      <div className="pt-1">
        {editingPlanId && (
          <p className="mb-2 text-xs font-medium" style={{ color: '#b89968' }}>
            Editing: {editingLabel}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            type="text"
            value={planName}
            onChange={e => setPlanName(e.target.value)}
            placeholder="Plan name (optional)"
            className={inputClass + ' sm:flex-1'}
          />
          <div className="flex items-center gap-3 sm:ml-auto">
            {editingPlanId && (
              <button
                type="button"
                onClick={resetPlanner}
                className="text-xs whitespace-nowrap transition-colors"
                style={{ color: 'var(--color-sub)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'rgb(var(--sl-100))')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-sub)')}
              >
                Cancel edit
              </button>
            )}
            <button
              type="button"
              onClick={handleSavePlan}
              disabled={!canSave}
              className="flex items-center justify-center gap-2 rounded-lg font-medium transition-colors whitespace-nowrap"
              style={{
                backgroundColor: '#d4b483',
                color: '#0c0e12',
                padding: '9px 16px',
                fontSize: 12,
                fontWeight: 500,
                opacity: canSave ? 1 : 0.4,
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}
            >
              {editingPlanId ? 'Update plan' : 'Save plan'}
            </button>
          </div>
        </div>
        {!canSave && (
          <p className="mt-2 text-xs text-slate-500">
            Pick an asset, set a budget, and make tiers total 100% to save this plan.
          </p>
        )}
      </div>
    </>
  );

  const toast = savedToast && (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 shadow-xl shadow-black/30">
      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/15 flex-shrink-0">
        <Check className="w-3.5 h-3.5 text-emerald-400" />
      </span>
      <p className="text-sm text-slate-100 font-medium">{savedToastMsg}</p>
    </div>
  );

  if (embedded) {
    return <div ref={rootRef} className="space-y-4">{inner}{toast}</div>;
  }

  return (
    <div ref={rootRef} className="rounded-2xl bg-slate-800/40 border border-slate-700 p-5 space-y-4">
      <CardHeader
        title="Split Buy Planner"
        subtitle="Plan staged entries across multiple price levels"
      />
      {inner}
      {toast}
    </div>
  );
}
